/**
 * 부트스트랩 & 시스템 조립 (전 FR 통합).
 * WASM(Rapier) 초기화 → 씬/물리/게임플레이/입력/오디오/UI 배선 → 메인 루프 시작.
 */
import './ui/styles.css';
import * as THREE from 'three';

import { EventBus } from './core/EventBus';
import { Clock } from './core/Clock';
import { GameLoop } from './core/loop';
import { IdleGate } from './core/idleGate';
import { GameState } from './core/GameState';
import { DevOverlay } from './core/devOverlay';

import { PhysicsWorld } from './physics/PhysicsWorld';
import { SceneManager } from './render/SceneManager';
import { PostFX } from './render/postfx';
import { ScreenShake } from './render/shake';
import { DeskCracks } from './render/DeskCracks';

import { ObjectSpawner } from './gameplay/ObjectSpawner';
import { preloadModels } from './render/modelLoader';
import { OBJECT_CATALOG } from './config/objects';
import { DebrisPool } from './gameplay/DebrisPool';
import { DeskBreak } from './gameplay/DeskBreak';
import { DestructionSystem } from './gameplay/DestructionSystem';
import { ScoreSystem } from './gameplay/ScoreSystem';
import { Session } from './gameplay/Session';
import { RageMoves } from './gameplay/RageMoves';

import { InputController } from './input/InputController';
import { RageGestures } from './input/RageGestures';
import { TrajectoryGuide } from './input/trajectory';
import { AudioBus } from './audio/AudioBus';

import { StartQuestion } from './ui/StartQuestion';
import { Hud } from './ui/Hud';
import { RageDock } from './ui/RageDock';
import { ResultCard } from './ui/ResultCard';
import { Settings } from './ui/Settings';

import { LAYOUT } from './config/layout';
import { t } from './i18n/i18n';
import { applyLocalizedMeta } from './i18n/meta';

async function bootstrap(): Promise<void> {
  // 접속자 언어에 맞춰 문서 타이틀/설명/OG·Twitter 메타/공유 카드(og-image) 반영.
  applyLocalizedMeta();

  const canvas = document.getElementById('scene') as HTMLCanvasElement;
  const uiRoot = document.getElementById('ui-root') as HTMLElement;

  const bus = new EventBus();
  const clock = new Clock(bus);

  const physics = await PhysicsWorld.create();
  const deskBody = buildStaticColliders(physics);

  const sceneMgr = new SceneManager(canvas);
  const postfx = new PostFX(sceneMgr.renderer, sceneMgr.scene, sceneMgr.camera);
  const shake = new ScreenShake(sceneMgr.camera, sceneMgr.baseCameraPos, bus);
  const deskCracks = new DeskCracks(sceneMgr.scene, bus); // 중량 물건 파괴 지점에 금 데칼

  // GLB 에셋이 있으면 프리로드(없으면 절차적 폴백).
  const modelMap = await preloadModels(OBJECT_CATALOG);
  const spawner = new ObjectSpawner(physics, sceneMgr.scene, modelMap);
  // 부술 수 없는 정적 데스크 소품(화분/노트/코스터/피규어)은 제거 — 마우스 패드(SceneManager)만 유지.
  const debris = new DebrisPool(physics, sceneMgr.scene);
  const destruction = new DestructionSystem(spawner, debris, bus, clock);
  // 책상 반파: desk:destroyed → 상판 반쪽 붕괴 + 콜라이더 교체, 세션 리셋 시 복원.
  const deskBreak = new DeskBreak(
    physics,
    sceneMgr.scene,
    bus,
    sceneMgr.deskMesh,
    sceneMgr.deskPadMesh,
    deskBody,
    () => makeDeskCollider(physics),
    debris,
    () => clock.now,
  );

  const score = new ScoreSystem(bus, clock);
  const session = new Session(bus, score);
  const state = new GameState(session, score, spawner);

  const audioBus = new AudioBus(bus);
  const trajectory = new TrajectoryGuide(sceneMgr.scene);
  // 레이지 무브: 쓸어버리기/책상 뒤집기 — 좌측 독(RageDock)에서 모드를 켜고 제스처로 발동.
  const rage = new RageMoves(spawner, bus);
  const gestures = new RageGestures(
    canvas,
    sceneMgr.camera,
    sceneMgr.scene,
    sceneMgr.deskMesh,
    sceneMgr.deskPadMesh,
    rage,
    bus,
    () => state.canInteract,
  );
  // 레이지 모드가 켜져 있는 동안 일반 잡기/호버는 비활성(제스처가 포인터를 소유).
  new InputController(canvas, sceneMgr.camera, spawner, physics, bus, trajectory, () => state.canInteract && gestures.mode === null);

  // UI
  const settings = new Settings(bus);
  const hud = new Hud(uiRoot, bus, session, {
    setVolume: (v) => settings.setVolume(v),
    getVolume: () => settings.volume,
  });
  new RageDock(uiRoot, bus, gestures);
  new ResultCard(uiRoot, bus, () => {
    // 다시하기: 결과 카드 닫고 시작 질문 화면으로.
    hud.setVisible(false);
    startQuestion.show();
  });
  const startQuestion = new StartQuestion(uiRoot, session);
  const dev = new DevOverlay(sceneMgr.renderer, physics);

  // 초기 프리뷰 배치(온보딩 즉시 책상이 보이도록).
  spawner.spawnAll();
  hud.setVisible(false);

  // "남은 물건" HUD: 책상 위 온전한 부술 물건 수. 파괴/리셋 때마다 갱신.
  const emitObjectsRemaining = (): void =>
    bus.emit('objects:remaining', { remaining: spawner.intactCount, total: spawner.objects.size });
  bus.on('object:broken', emitObjectsRemaining);
  emitObjectsRemaining();

  // --- 세션/리셋 배선 ---
  // 시작(session:start)과 리셋(session:reset)은 월드 초기화 로직이 동일하고, 시작만
  // 추가로 HUD를 켠다.
  let ended = false;
  const resetWorld = (): void => {
    spawner.resetAll();
    debris.clear();
    ended = false;
    emitObjectsRemaining();
  };
  bus.on('session:reset', resetWorld);
  bus.on('session:start', () => {
    resetWorld();
    hud.setVisible(true);
  });
  bus.on('session:end', () => {
    hud.setVisible(false);
  });

  // --- 루프 ---
  // 유휴 렌더 게이트: 유휴 화면(시작 질문/결과 카드)에서 정적 프레임을 계속 그리지 않도록,
  // 시각 변화 신호가 사라지고 settle 창이 지나면 렌더를 건너뛴다(캔버스는 마지막 프레임 유지).
  // 물리/게임 로직은 계속 돈다(전부 sleep 상태라 저비용) — 신호가 생기면 즉시 렌더 재개.
  const idleGate = new IdleGate();
  let renderedFrames = 0;
  const loop = new GameLoop(
    clock,
    () => {
      // 고정 스텝: 쓸기 배팅(팔이 지나간 물건) + 잡은 물건 책상 관통 검사 + 물리 1스텝 + 충돌.
      rage.stepSweep();
      destruction.checkGrabbedDeskSmash();
      const pairs = physics.step();
      destruction.process(pairs, clock.now);
    },
    (dt) => {
      score.update(); // 콤보 만료
      debris.update(clock.now);
      spawner.syncMeshes();
      deskBreak.sync(); // 반쪽 슬랩 낙하 동기화(파괴 시에만 개체 존재)

      // 종료: 책상 위 물건을 모두 부수면 세션 종료 → 결과 카드(1회).
      if (state.allBroken && !ended) {
        ended = true;
        session.finish();
      }

      gestures.update(dt); // 레이지 제스처 연출(팔 애니메이션/책상 기울기)
      shake.update(dt); // 카메라 위치 갱신(렌더 전)

      // 활동 신호: 플레이 중(입력 반응성 우선 — 항상 렌더) / 파편 잔존(낙하·페이드) /
      // 셰이크 진행 / 깨어있는 동적 바디(스폰 정착, 책상 슬랩 낙하). 제스처(팔/기울기)는
      // 플레이 중에만 존재하고 세션 종료 시 cancelGesture로 원복되므로 별도 신호가 불필요.
      const visualActivity =
        state.canInteract || debris.count > 0 || shake.active || physics.anyDynamicAwake();
      if (idleGate.shouldRender(visualActivity, performance.now() / 1000)) {
        postfx.render(sceneMgr.scene, sceneMgr.camera);
        renderedFrames++;
      }
      dev.update(dt);
    },
  );

  window.addEventListener('resize', () => {
    sceneMgr.resize();
    postfx.resize();
    idleGate.wake(performance.now() / 1000); // 리사이즈는 캔버스를 비우므로 재렌더 필수
  });
  // 탭 복귀 시 웨이크 — 게이트 중 브라우저가 캔버스 내용을 버렸을 수 있어 한 번 다시 그린다.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) idleGate.wake(performance.now() / 1000);
  });
  // GPU 프로세스 리셋 등으로 컨텍스트가 유실→복구되면 드로잉 버퍼가 빈 상태다. three는
  // GL 재초기화만 하고 재도색은 다음 render() 몫인데, 게이트가 닫혀 있으면 렌더가 없어
  // 캔버스가 블랭크로 남는다(리사이즈/탭 전환 전까지) — 복구 시점에 웨이크해 다시 그린다.
  canvas.addEventListener('webglcontextrestored', () => idleGate.wake(performance.now() / 1000));

  // 개발용: ?debug에서 모든 오브젝트를 즉시 파괴해 파편 재질을 확인.
  if (new URLSearchParams(location.search).has('debug')) {
    const counters = { broken: 0, impact: 0, debrisImpact: 0 };
    bus.on('object:broken', () => counters.broken++);
    bus.on('object:impact', () => counters.impact++);
    bus.on('debris:impact', () => counters.debrisImpact++);
    (window as unknown as { __desksmash?: unknown }).__desksmash = {
      audio: audioBus,
      /** 상태 스냅샷(검증용) + 이벤트 카운터 + 클록 + 금 데칼/책상 상태. */
      state() {
        return {
          ...state.snapshot(),
          ...counters,
          clockNow: +clock.now.toFixed(2),
          timeScale: clock.timeScale,
          cracks: deskCracks.count,
          deskDestroyed: deskBreak.isDestroyed,
          rendered: renderedFrames,
          renderGated: idleGate.gated,
        };
      },
      /**
       * 실제 물리 경로 검증: 첫 온전한 오브젝트를 위로 띄웠다가 지정 속도로 책상에 내리꽂는다.
       * 파괴는 충돌 이벤트 → DestructionSystem 경로로만 일어남(직접 파괴 아님).
       */
      throwAtDesk(speed = 10) {
        const obj = [...spawner.objects.values()].find((o) => o.state === 'intact');
        if (!obj) return null;
        const pos = obj.body.translation();
        obj.body.setTranslation({ x: pos.x, y: pos.y + 0.8, z: pos.z }, true);
        obj.body.setLinvel({ x: 0, y: -speed, z: 0 }, true);
        return obj.def.id;
      },
      inspect() {
        const out: string[] = [];
        for (const obj of spawner.objects.values()) {
          const mats: string[] = [];
          obj.mesh.traverse((o) => {
            const m = o as THREE.Mesh;
            if (m.isMesh) {
              const mat = m.material as THREE.MeshStandardMaterial;
              mats.push(`${(mat.name || '?')}:#${mat.color?.getHexString?.() ?? '??'}`);
            }
          });
          out.push(`${obj.def.id}[${obj.mesh.type}] ${mats.join(',')}`);
        }
        return out;
      },
      /** 쓸기 배팅 직접 트리거(팔 속도 armSpeed로 전 물건 타격 — 살살/세게 비교용). */
      sweep(dir: 1 | -1 = 1, armSpeed = 12) {
        rage.debugSweep(dir, armSpeed);
      },
      flip() {
        rage.flip();
      },
      shatterAll() {
        for (const obj of [...spawner.objects.values()]) {
          if (obj.state === 'broken') continue;
          // forceBreak가 object:broken을 발행 → 점수/사운드/셰이크까지 트리거.
          destruction.forceBreak(obj.instanceId, { x: 0, y: 1, z: 0 }, 12);
        }
      },
    };
  }

  loop.start();
  startQuestion.show();
}

// 책상 상판 콜라이더: 시각 슬랩(size[1]=0.06 얇음)과 달리 아래로 두껍게(0.4) 만든다.
// 얇은 콜라이더면 상판 표면(y≈0)에서 스폰된 파편이 콜라이더 아래쪽에 걸려 바닥면으로
// 밀려나 책상을 통과한다 → 두꺼운 콜라이더는 항상 위로 밀어내 상판에 안착시킨다.
// 윗면은 topY(=0)에 정확히 맞춰 오브젝트 안착 높이는 그대로 유지.
const DESK_COLLIDER_H = 0.4;

/** 책상 정적 콜라이더 생성 — 최초 배선과 책상 반파 후 복원(DeskBreak)이 공유. */
function makeDeskCollider(physics: PhysicsWorld): ReturnType<PhysicsWorld['addStaticCuboid']> {
  return physics.addStaticCuboid(
    { x: 0, y: LAYOUT.desk.topY - DESK_COLLIDER_H / 2, z: 0 },
    { x: LAYOUT.desk.size[0] / 2, y: DESK_COLLIDER_H / 2, z: LAYOUT.desk.size[2] / 2 },
  );
}

/** LAYOUT 치수로 정적 콜라이더 생성(SceneManager 메쉬와 동일 좌표). 책상 body를 반환. */
function buildStaticColliders(physics: PhysicsWorld): ReturnType<PhysicsWorld['addStaticCuboid']> {
  const half = (s: [number, number, number]): THREE.Vector3Like => ({ x: s[0] / 2, y: s[1] / 2, z: s[2] / 2 });
  const deskBody = makeDeskCollider(physics);
  // 바닥
  physics.addStaticCuboid({ x: 0, y: LAYOUT.floor.y, z: LAYOUT.floor.z }, half(LAYOUT.floor.size));
  // 뒷벽
  physics.addStaticCuboid({ x: 0, y: LAYOUT.backWall.y, z: LAYOUT.backWall.z }, half(LAYOUT.backWall.size));
  // 옆벽
  physics.addStaticCuboid({ x: LAYOUT.sideWalls.x, y: LAYOUT.sideWalls.y, z: LAYOUT.sideWalls.z }, half(LAYOUT.sideWalls.size));
  physics.addStaticCuboid({ x: -LAYOUT.sideWalls.x, y: LAYOUT.sideWalls.y, z: LAYOUT.sideWalls.z }, half(LAYOUT.sideWalls.size));
  // 천장(위로 던진 오브젝트가 프레임 밖으로 사라지지 않고 부딪혀 부서지도록) — kind 'ceiling'으로
  // 태그해 DestructionSystem이 천장 충돌을 즉시 파괴로 처리한다.
  physics.addStaticCuboid({ x: 0, y: LAYOUT.ceiling.y, z: LAYOUT.ceiling.z }, half(LAYOUT.ceiling.size), 'ceiling');
  // 앞턱(파편 이탈 방지)
  physics.addStaticCuboid({ x: 0, y: LAYOUT.frontLedge.y, z: LAYOUT.frontLedge.z }, half(LAYOUT.frontLedge.size));
  return deskBody;
}

bootstrap().catch((err) => {
  console.error('[Desk Smash] 초기화 실패:', err);
  const root = document.getElementById('ui-root');
  if (root) {
    const msg = document.createElement('div');
    msg.className = 'overlay';
    const card = document.createElement('div');
    card.className = 'card';
    // WASM 실행 불가 환경 감지 — 물리엔진(Rapier WASM)이 필수라 이 경우 일반 메시지 대신
    // 원인·해결법을 안내. 차단 형태가 둘이다:
    //  (a) WebAssembly 전역 자체가 제거됨 (Safari 차단 모드 등)
    //  (b) 전역은 있으나 CSP/보안 모드/확장이 컴파일을 차단 → CompileError
    //      ("Wasm code generation disallowed by embedder" 등)
    const wasmDisabled =
      typeof WebAssembly === 'undefined' ||
      err instanceof WebAssembly.CompileError ||
      /webassembly|wasm/i.test(err instanceof Error ? err.message : String(err));
    card.textContent = wasmDisabled ? t('initErrorWasm') : t('initError');
    msg.appendChild(card);
    root.appendChild(msg);
  }
});
